/**
 * This file may contain code that utilizes generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub CoPilot, OpenAI ChatGPT or others.
 */
import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import cn from 'classnames';

import { Divider, Box, Collapse, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIosRounded from '@mui/icons-material/ArrowBackIosRounded';
import ChatOutlined from '@mui/icons-material/ChatOutlined';
import Menu from '@mui/icons-material/Menu';
import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';

import { DrawerProvider } from '../../components/drawer';
import { Translate, useLocalizationContext } from '../../components/localization';
import { MaterialIcon } from '../../components/icons';
import { AppSwitcher } from './app-switcher/AppSwitcher';
import { Profile } from './profile/Profile';
import MenuIcon from './menu-icon/MenuIcon';
import { AppContext } from '../../util/app-provider/context';
import EntityPicker from './entity-picker';
import ProgressBar from './progress-bar';
import SearchField from './search-field';
import TitleMenu from './title-menu';
import { HeaderContainer } from './style';

import { useChatContext } from '../../components/chat';
import { useCommandIntelligence } from '../../components/command-intelligence/CommandIntelligenceContext';
import CommandIntelligenceHeaderButton from '../../components/command-intelligence/CommandIntelligenceHeaderButton.jsx';
import useQueryControls from './useQueryControls';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { useDebounce } from '../../hooks/useDebounce';
import { usePostMessageContext } from '../../post-message/PostMessage';
import { usePostMessageListener } from '../../util/entity-provider/usePostMessageListener';
import { featureEnvironmentKeys, isFeatureEnvironment } from '../../util/featureFlagEnvironments';

const learningCenterAppRef = 'learning-center';
const platformAdminAppRef = 'admin';
const maxUnreadMsgCounter = '99+';
const globalSearchAppRefs = [
  'dashboards-exp',
  'ar-exp',
  'batch-exp',
  'billing-exp',
  'carbon-accounting-exp',
  'company-admin',
  'dispatch-exp',
  'driver-management',
  'supply-exp',
  'sales-quote-exp',
  'bulk-materials-exp',
  'trackit-exp',
];

export const Header = ({
  className,
  appContext,
  entityContext,
  userContext,
  onChange,
  onProfileClick,
  disableAppTray,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 300);

  const { enabled: commandIntelligenceEnabled, openBlank: openCommandIntelligence } = useCommandIntelligence();
  const { entitySetup } = entityContext;
  const { userApps, userAppsIsLoading, user, hasPermission } = userContext;
  const { isXl, isMd } = useBreakpoints();
  const { sendMessage } = usePostMessageContext();
  const { setParentAppMessages, parentAppMessages, userLocale } = useLocalizationContext();

  useEffect(() => {
    sendMessage({ type: 'merge-parent-messages' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMessageEvent = useCallback(
    message => {
      if (message.type === 'set-merge-parent-messages') {
        setParentAppMessages(message.payload);
      }
    },
    [setParentAppMessages]
  );

  usePostMessageListener(handleMessageEvent);

  const cdsContext = useContext(AppContext);
  const { handleChatOpen, isChatOpened, handleChatClose, showNotification, totalUnreadMessages } = useChatContext();
  const {
    appEntities,
    currentFeature,
    headerText,
    headerControls,
    appRef,
    headerConfig: contextHeaderConfig,
  } = appContext;
  const { entityRef, setEntityRef } = entityContext;
  const headerConfig = useMemo(() => (cdsContext && contextHeaderConfig) || {}, [contextHeaderConfig, cdsContext]);
  const { searchControl, dateControl } = useQueryControls(headerConfig?.queryControls || []);

  const formattedUnreadMessagesCount = totalUnreadMessages > 99 ? maxUnreadMsgCounter : totalUnreadMessages?.toString();
  const disableMobileSidebar = !cdsContext || !cdsContext.toggleSidebar;
  const isSuccessCenter = appRef === learningCenterAppRef;
  const isPlatformAdmin = appRef === platformAdminAppRef;
  const isGlobalSearchAllowed = globalSearchAppRefs.includes(appRef);

  const hasChatSKU = useMemo(() => {
    const entitlements = entityContext?.entitlements?.entitlements || [];
    return entitlements?.some?.(etl => etl?.skuRef === 'chat-preview');
  }, [entityContext]);

  const hasChatPermission = useMemo(() => {
    return hasPermission('chat-view');
  }, [hasPermission]);

  const headerTitle = useMemo(() => {
    return (
      <>
        {(currentFeature?.icon || currentFeature?.iconName) && (
          <MaterialIcon icon={currentFeature?.icon} iconName={currentFeature?.iconName} className="feature-icon" />
        )}
        {headerText ||
          (currentFeature ? (
            <Translate
              data-testid="feature-title"
              stringId={currentFeature.labelStringId}
              className={cn('feature-title')}
            />
          ) : (
            ''
          ))}
      </>
    );
  }, [currentFeature, headerText]);

  useEffect(() => {
    const payload = { type: 'global-search-term', term: debouncedSearch || '', entityRef };
    try {
      const target = window.parent && window.parent !== window ? window.parent : window;
      target.postMessage(payload, '*');
    } catch (e) {}
  }, [debouncedSearch, entityRef]);

  const showChatIcon = (!isSuccessCenter || !isPlatformAdmin) && hasChatPermission && hasChatSKU;
  const showGlobalSearch = isGlobalSearchAllowed && (isXl || isMd);

  const handleEntityChange = useCallback(
    (entityRef, _companyName) => {
      setEntityRef(entityRef);
      handleChatClose();
    },
    [setEntityRef, handleChatClose]
  );

  const toggleSearch = () => {
    setIsSearchOpen(prevState => !prevState);
  };

  const featureIcon =
    headerConfig?.tabsConfig?.customFeatureIcon ||
    ((currentFeature?.icon || currentFeature?.iconName) && (
      <MaterialIcon icon={currentFeature?.icon} iconName={currentFeature?.iconName} className="feature-icon" />
    ));

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform || '');
  const shortcutLabel = isMac ? '⌘K' : '⌃K';

  const openGlobalSearch = useCallback(() => {
    if (!isGlobalSearchAllowed) return;
    try {
      const openEverywhere = w => {
        try {
          if (!w) return;
          const CE = w.CustomEvent || CustomEvent;
          w.dispatchEvent(new CE('open-global-search-modal', { detail: {} }));
          w.document && w.document.dispatchEvent(new CE('open-global-search-modal', { detail: {} }));
          w.document &&
            w.document.body &&
            w.document.body.dispatchEvent(new CE('open-global-search-modal', { detail: {} }));
          w.postMessage && w.postMessage({ type: 'open-global-search-modal' }, '*');
        } catch (e) {}
      };
      openEverywhere(window);
      if (window.parent && window.parent !== window) {
        openEverywhere(window.parent);
        try {
          window.parent.postMessage({ type: 'open-global-search-modal' }, '*');
        } catch (e) {}
      }
      if (window.top && window.top !== window && window.top !== window.parent) {
        openEverywhere(window.top);
        try {
          window.top.postMessage({ type: 'open-global-search-modal' }, '*');
        } catch (e) {}
      }
    } catch (e) {}
  }, [isGlobalSearchAllowed]);

  useEffect(() => {
    if (!isGlobalSearchAllowed) return;
    // Global search accepts only the plain ⌘K / ⌃K chord. Shift or Alt with K is not a valid combo here,
    // so embedded apps can reserve Cmd/Ctrl+Shift+K (and Alt variants) without connex-cds opening search first.
    const handler = e => {
      const key = (e.key || '').toLowerCase();
      if (key !== 'k') return;
      if (e.shiftKey || e.altKey) return;
      const macCombo = isMac && e.metaKey && !e.ctrlKey;
      const winCombo = !isMac && e.ctrlKey && !e.metaKey;
      if (macCombo || winCombo) {
        e.preventDefault();
        e.stopPropagation();
        openGlobalSearch();
      }
    };

    const opts = { capture: true };
    window.addEventListener('keydown', handler, opts);
    document.addEventListener('keydown', handler, opts);
    document.body && document.body.addEventListener('keydown', handler, opts);

    // Also listen on keyup in case keydown is intercepted by a nested handler
    const handlerUp = e => {
      const key = (e.key || '').toLowerCase();
      if (key !== 'k') return;
      if (e.shiftKey || e.altKey) return;
      const macCombo = isMac && e.metaKey && !e.ctrlKey;
      const winCombo = !isMac && e.ctrlKey && !e.metaKey;
      if (macCombo || winCombo) {
        e.preventDefault();
        e.stopPropagation();
        openGlobalSearch();
      }
    };
    window.addEventListener('keyup', handlerUp, opts);
    document.addEventListener('keyup', handlerUp, opts);
    document.body && document.body.addEventListener('keyup', handlerUp, opts);

    return () => {
      window.removeEventListener('keydown', handler, opts);
      document.removeEventListener('keydown', handler, opts);
      document.body && document.body.removeEventListener('keydown', handler, opts);
      window.removeEventListener('keyup', handlerUp, opts);
      document.removeEventListener('keyup', handlerUp, opts);
      document.body && document.body.removeEventListener('keyup', handlerUp, opts);
    };
  }, [isMac, openGlobalSearch, isGlobalSearchAllowed]);

  return (
    <HeaderContainer className={cn({ 'is-mobile': !isXl, 'disable-mobile-sidebar': disableMobileSidebar })}>
      <DrawerProvider>
        <div className={cn('header', className)}>
          {!isXl && !isMd && cdsContext && dateControl && (
            <Box {...(!isMd && { sx: { marginTop: '0.5rem' } })}>{dateControl.component}</Box>
          )}

          {!isXl && !isMd && cdsContext && searchControl && (
            <Collapse in={isSearchOpen}>
              <SearchField
                component={searchControl.component}
                props={{ ...searchControl.props, toggleSearch }}
                sx={{ width: '100%' }}
              />
            </Collapse>
          )}

          {!headerConfig?.hideFeatureName && (
            <Box
              className={cn('left', { visible: !!currentFeature?.id })}
              {...((headerConfig?.queryControls || headerConfig?.actions) && {
                sx: { display: 'flex', justifyContent: 'space-between' },
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexGrow: 1 }}>
                {!isXl && cdsContext && headerConfig?.onArrowBackClick && (
                  <MaterialIcon
                    icon={ArrowBackIosRounded}
                    className={'arrow-back-icon'}
                    onClick={headerConfig?.onArrowBackClick}
                    sx={{ height: '28px', width: '28px' }}
                  />
                )}
                <div data-testid="feature-name" className={cn('feature-name')}>
                  {!isXl && headerConfig?.tabsConfig && headerConfig.tabsConfig?.tabs?.length > 0 ? (
                    <TitleMenu
                      menuItems={headerConfig.tabsConfig.tabs}
                      setCurrentTab={headerConfig.tabsConfig.setCurrentTab}
                      currentTab={headerConfig.tabsConfig.currentTab}
                      icon={featureIcon}
                    />
                  ) : (
                    headerTitle
                  )}
                </div>
                {!isXl && cdsContext && headerConfig?.progressBarValue && (
                  <ProgressBar value={headerConfig?.progressBarValue} className="progress-bar" />
                )}
                {isMd && !isXl && cdsContext && searchControl && (
                  <SearchField component={searchControl.component} props={{ ...searchControl.props, toggleSearch }} />
                )}
              </Box>

              {headerControls}

              {!isXl && cdsContext && headerConfig && (headerConfig?.queryControls || headerConfig?.actions) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: isSearchOpen ? '0' : '10px' }}>
                  {headerConfig && headerConfig?.actions?.length > 0 && (
                    <Box className="action-buttons" sx={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                      {headerConfig.actions}
                    </Box>
                  )}

                  {headerConfig?.queryControls?.length > 0 && (
                    <Box className="query-controls" sx={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                      {isMd && dateControl?.component}
                      {!isMd && !isXl && cdsContext && searchControl && (
                        <Collapse in={!isSearchOpen} orientation="horizontal">
                          <IconButton
                            onClick={toggleSearch}
                            aria-label="expand-search"
                            className="collapse-search-button"
                          >
                            <SearchIcon />
                          </IconButton>
                        </Collapse>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {!isXl && cdsContext && !headerConfig?.hideFeatureName && (
            <Divider component="hr" className="header-divider" aria-hidden="true" />
          )}

          <div className="right">
            <div className="center-aligned">
              {!disableMobileSidebar && cdsContext.toggleSidebar && (
                <MenuIcon
                  icon={Menu}
                  onClick={cdsContext.toggleSidebar}
                  variant="secondary"
                  className={cn('mobile-menu-icon')}
                />
              )}
              {!disableAppTray && (
                <>
                  {showGlobalSearch && (
                    <div className="global-search-icon-container" title={`Search [${shortcutLabel}]`}>
                      <MenuIcon
                        icon={SearchIcon}
                        onClick={() => {
                          try {
                            openGlobalSearch();
                          } catch (e) {}
                        }}
                        variant="secondary"
                      ></MenuIcon>
                    </div>
                  )}
                  {commandIntelligenceEnabled && (
                    <CommandIntelligenceHeaderButton
                      onClick={() => openCommandIntelligence()}
                      title={parentAppMessages[userLocale]?.command_intelligence_drawer_title}
                    />
                  )}
                  {isFeatureEnvironment(featureEnvironmentKeys.supportChat) && (
                    <div
                      title={parentAppMessages[userLocale]?.chatSupport_availability || 'Mon-Fri, 8 AM - 4 PM Central'}
                    >
                      <MenuIcon
                        icon={SupportAgentOutlined}
                        onClick={() => sendMessage({ type: 'open-support-chat' })}
                        variant="secondary"
                      />
                    </div>
                  )}
                  {showChatIcon && (
                    <div className="chat-icon-container">
                      <MenuIcon
                        active={isChatOpened}
                        icon={ChatOutlined}
                        onClick={isChatOpened ? handleChatClose : handleChatOpen}
                        variant="secondary"
                      >
                        {showNotification && (
                          <div
                            className={cn('chat-icon-badge', {
                              'max-unread-messages ': formattedUnreadMessagesCount === maxUnreadMsgCounter,
                            })}
                          >
                            {formattedUnreadMessagesCount}
                          </div>
                        )}
                      </MenuIcon>
                    </div>
                  )}
                  <AppSwitcher
                    onChange={onChange}
                    userApps={userApps}
                    isLoading={userAppsIsLoading}
                    appContext={appContext}
                    entityContext={entityContext}
                    parentAppMessages={parentAppMessages}
                    userLocale={userLocale}
                  />
                  {appEntities?.length > 0 && (
                    <EntityPicker
                      currentEntitySetup={entitySetup}
                      value={entityRef}
                      onChange={handleEntityChange}
                      entities={appEntities}
                      className="entity-picker"
                    />
                  )}
                </>
              )}
              <Profile onClick={onProfileClick} user={user} />
            </div>
          </div>
        </div>
      </DrawerProvider>
    </HeaderContainer>
  );
};
